import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Input, Select, message } from 'antd';
import { useQueryClient } from '@tanstack/react-query';
import AccentButton from '../components/AccentButton/AccentButton';
import { list_bands, create_band, Band } from '../api';
import { useAuth } from '../contexts/AuthContext';
import './AddBand.css';

const AddBand: React.FC = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();
    const { refreshAuth } = useAuth();
    const [bandName, setBandName] = useState('');
    const [genres, setGenres] = useState<string[]>([]);
    const [city, setCity] = useState('Tulsa');
    const [state, setState] = useState('OK');
    const [checking, setChecking] = useState(false);
    const [creating, setCreating] = useState(false);
    const [existingBands, setExistingBands] = useState<Band[]>([]);
    const [availability, setAvailability] = useState<{
        available: boolean;
        match?: Band;
    } | null>(null);
    const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    // Load existing bands on mount
    useEffect(() => {
        list_bands().then(setExistingBands).catch(console.error);
    }, []);

    // Check band name availability
    useEffect(() => {
        if (debounceRef.current) clearTimeout(debounceRef.current);

        const trimmed = bandName.trim();
        if (!trimmed) {
            setAvailability(null);
            setChecking(false);
            return;
        }

        setChecking(true);
        debounceRef.current = setTimeout(() => {
            const normalised = trimmed.toLowerCase();
            const match = existingBands.find(
                (b) => b.name.toLowerCase() === normalised
            );
            setAvailability(match ? { available: false, match } : { available: true });
            setChecking(false);
        }, 300);

        return () => {
            if (debounceRef.current) clearTimeout(debounceRef.current);
        };
    }, [bandName, existingBands]);

    const canCreate = !checking && !creating && availability?.available === true &&
        bandName.trim().length > 0 && genres.length > 0 && city.trim().length > 0 && state.trim().length > 0;

    const handleCreate = async () => {
        if (!canCreate) return;

        setCreating(true);
        try {
            const newBand = await create_band({
                name: bandName.trim(),
                genres,
                city: city.trim(),
                state: state.trim(),
            });

            // Refresh auth to get updated claims with new band role
            try {
                await refreshAuth();
            } catch (refreshError) {
                console.error('Failed to refresh auth:', refreshError);
                // Don't block navigation if refresh fails
            }

            // Invalidate queries to refetch fresh data
            await queryClient.invalidateQueries({ queryKey: ['bands'] });

            message.success('Band created successfully!');
            navigate(`/bands/${newBand.id}`);
        } catch (error: any) {
            message.error(error.message || 'Failed to create band');
        } finally {
            setCreating(false);
        }
    };

    const genreOptions = [
        'Rock', 'Blues', 'Jazz', 'Country', 'Folk', 'Indie', 'Alternative',
        'Metal', 'Punk', 'Hip Hop', 'R&B', 'Soul', 'Funk', 'Electronic',
        'Pop', 'Americana', 'Bluegrass', 'Gospel', 'Reggae', 'World'
    ].map(g => ({ label: g, value: g }));

    return (
        <div className="addband-page">
            <div className="addband-content">
                <h1 className="addband-title">Add Band</h1>

                <div className="addband-form">
                    <div className="addband-field">
                        <label className="addband-label">Band Name</label>
                        <Input
                            placeholder="Band Name"
                            value={bandName}
                            onChange={(e) => setBandName(e.target.value)}
                        />
                    </div>

                    <div className="addband-availability">
                        <span className="addband-availability-label">Availability</span>
                        <div className="addband-availability-status">
                            {!checking && availability === null && (
                                <span className="addband-availability-idle">Start typing to check name</span>
                            )}
                            {checking && (
                                <span className="addband-availability-checking">Checking…</span>
                            )}
                            {!checking && availability?.available === true && (
                                <span className="addband-availability-available">
                                    Name is available 👍
                                </span>
                            )}
                            {!checking && availability?.available === false && availability.match && (
                                <div className="addband-availability-taken-group">
                                    <span className="addband-availability-hint">Name match found:</span>
                                    <span className="addband-availability-taken-name">
                                        {availability.match.name}
                                    </span>
                                    <button
                                        className="addband-availability-link"
                                        onClick={() => navigate(`/bands/${availability.match!.id}`)}
                                    >
                                        👉 View Band
                                    </button>
                                    <button
                                        className="addband-availability-link"
                                        onClick={() => navigate('/contact')}
                                    >
                                        👉 Contact Us
                                    </button>
                                </div>
                            )}
                        </div>
                    </div>

                    <div className="addband-field">
                        <label className="addband-label">Genres</label>
                        <Select
                            mode="multiple"
                            placeholder="Select genres"
                            value={genres}
                            onChange={setGenres}
                            options={genreOptions}
                            style={{ width: '100%' }}
                        />
                    </div>

                    <div className="addband-field">
                        <label className="addband-label">City</label>
                        <Input
                            placeholder="City"
                            value={city}
                            onChange={(e) => setCity(e.target.value)}
                        />
                    </div>

                    <div className="addband-field">
                        <label className="addband-label">State</label>
                        <Input
                            placeholder="State"
                            value={state}
                            onChange={(e) => setState(e.target.value)}
                            maxLength={2}
                        />
                    </div>

                    <AccentButton
                        size="large"
                        block
                        shape="round"
                        onClick={handleCreate}
                        disabled={!canCreate}
                        loading={creating}
                    >
                        Create Band
                    </AccentButton>
                </div>
            </div>
        </div>
    );
};

export default AddBand;
